import { commentConditionLabel, isCommentConditionValid } from './comment-condition.util';
import type { I18nService } from '../../../core';
import type { CustomFieldViewModel } from '../../custom-fields/models/custom-field.model';
import type {
  AutomationDelayConfig,
  AutomationSafetyMode,
  EmailTemplateRefModel,
  WhatsAppTemplateModel,
  WorkflowAutomationConditionModel,
  WorkflowAutomationActionType,
  WorkflowAutomationConditionField,
  WorkflowAutomationConditionOperator,
  WorkflowAutomationModel,
  WorkflowAutomationTriggerType,
  CreateWorkflowAutomationConditionRequestModel,
} from '../models/automation-api.models';
import { WHATSAPP_TEMPLATE_CATALOG } from './whatsapp-template.catalog';
import {
  hasWhatsAppVariablesFilled,
  isWhatsAppActionConfigReady,
  isWhatsAppDestinationComplete,
  normalizeWhatsAppDestinationType,
} from './whatsapp-action-config.utils';
import { resolveExpectedWhatsAppVariableKeys } from './whatsapp-template-resolve.utils';
import { isWhatsAppVariableSlotValueValid } from './whatsapp-template-variables.util';
import {
  isEmailAutomationConfigComplete,
  normalizeEmailRecipientType,
} from './email-action-config.utils';

export interface ConditionDisplayContext {
  workflowFields?: CustomFieldViewModel[];
  workflowStages?: { id: string; name: string }[];
}

export interface ActionDisplayContext {
  workflowFields?: CustomFieldViewModel[];
  workflowStages?: { id: string; name: string }[];
  members?: { id: string; name: string }[];
  whatsAppTemplates?: readonly WhatsAppTemplateModel[] | null;
  emailTemplates?: readonly EmailTemplateRefModel[] | null;
}

function emailTemplateDisplayName(
  templateKey: string,
  templates?: readonly EmailTemplateRefModel[] | null
): string {
  const key = templateKey.trim();
  if (!key) return '-';
  const api = templates?.find((t) => t.key === key);
  if (api?.name?.trim()) return api.name.trim();
  return key;
}

function emailRecipientSummaryLine(
  i18n: I18nService,
  config: Record<string, unknown>,
  workflowFields?: CustomFieldViewModel[]
): string {
  const dest = normalizeEmailRecipientType(config['recipientType'] ?? config['destinationType']);
  if (dest === 'FixedEmail') {
    const em = typeof config['targetEmail'] === 'string' ? config['targetEmail'].trim() : '';
    return i18n.t('features.automations.summary.emailToFixed', { email: em || '-' });
  }
  if (dest === 'PersonField' || dest === 'EmailField') {
    const fid =
      typeof config['recipientWorkflowFieldId'] === 'string'
        ? config['recipientWorkflowFieldId'].trim()
        : '';
    const fname = workflowFields?.find((f) => f.fieldId === fid)?.name ?? fid;
    return dest === 'PersonField'
      ? i18n.t('features.automations.summary.emailToPersonField', { field: fname || '-' })
      : i18n.t('features.automations.summary.emailToEmailField', { field: fname || '-' });
  }
  if (dest === 'CardAssignee') return i18n.t('features.automations.summary.emailToAssignee');
  if (dest === 'CardCreator') return i18n.t('features.automations.summary.emailToCreator');
  return '-';
}

function whatsappTemplateDisplayName(
  i18n: I18nService,
  templateKey: string,
  templates?: readonly WhatsAppTemplateModel[] | null
): string {
  const key = templateKey.trim();
  if (!key) return '-';
  const api = templates?.find((t) => t.key === key);
  if (api?.name?.trim()) return api.name.trim();
  const cat = WHATSAPP_TEMPLATE_CATALOG.find((c) => c.key === key);
  if (cat) return i18n.t(cat.labelKey);
  return key;
}

export function triggerLabel(i18n: I18nService, trigger: WorkflowAutomationTriggerType): string {
  const map: Record<WorkflowAutomationTriggerType, string> = {
    DailyAgenda: 'dailyagenda.trigger',
    CardCreated: 'features.automations.triggers.cardCreated',
    WorkflowFormSubmitted: 'features.automations.triggers.workflowFormSubmitted',
    CardMovedToStage: 'features.automations.triggers.cardEnteredStage',
    CardFieldUpdated: 'features.automations.triggers.cardFieldUpdated',
    ChecklistItemCompleted: 'features.automations.triggers.checklistItemCompleted',
    CardDueDateNear: 'features.automations.triggers.cardDueDateNear',
    CardDueDateOverdue: 'features.automations.triggers.cardDueDateOverdue',
  };
  return i18n.t(map[trigger], undefined, trigger);
}

export function actionLabel(i18n: I18nService, action: WorkflowAutomationActionType): string {
  const map: Record<WorkflowAutomationActionType, string> = {
    MoveCardToStage: 'features.automations.actionstypes.moveCardToStage',
    CreateCardComment: 'features.automations.actionstypes.createCardComment',
    AssignUserToCard: 'features.automations.actionstypes.assignUserToCard',
    SendWhatsAppTemplate: 'features.automations.actionstypes.sendWhatsAppShort',
    SendEmailTemplate: 'features.automations.actionstypes.sendEmailTemplate',
    UpdateCardField: 'features.automations.actionstypes.updateCardField',
    SetCardDueDate: 'features.automations.actionstypes.setCardDueDate',
    CreateCard: 'features.automations.actionstypes.createCard',
    SendAppNotification: 'features.automations.actionstypes.sendAppNotification',
    SendWhatsAppSessionMessage: 'features.automations.actionstypes.sendWhatsAppSessionMessage',
  };
  return i18n.t(map[action], undefined, action);
}

export function conditionFieldLabel(
  i18n: I18nService,
  field: WorkflowAutomationConditionField
): string {
  const map: Record<WorkflowAutomationConditionField, string> = {
    CardField: 'features.automations.conditionfields.cardField',
    CardStage: 'features.automations.conditionfields.cardStage',
    ChecklistCompleted: 'features.automations.conditionfields.checklistCompleted',
    CardComments: 'features.automations.comments.label',
  };
  return i18n.t(map[field], undefined, field);
}

export function conditionOperatorLabel(
  i18n: I18nService,
  operator: WorkflowAutomationConditionOperator
): string {
  const map: Record<WorkflowAutomationConditionOperator, string> = {
    Equals: 'features.automations.conditionoperators.equals',
    NotEquals: 'features.automations.conditionoperators.notEquals',
    GreaterThan: 'features.automations.conditionoperators.greaterThan',
    LessThan: 'features.automations.conditionoperators.lessThan',
    Contains: 'features.automations.conditionoperators.contains',
    GreaterThanOrEqual: 'features.automations.conditionoperators.greaterThanOrEqual',
    LessThanOrEqual: 'features.automations.conditionoperators.lessThanOrEqual',
    NotContains: 'features.automations.conditionoperators.notContains',
    IsEmpty: 'features.automations.conditionoperators.isEmpty',
    IsNotEmpty: 'features.automations.conditionoperators.isNotEmpty',
    ChangedTo: 'features.automations.conditionoperators.changedTo',
    ChangedFromTo: 'features.automations.conditionoperators.changedFromTo',
    BecameFilled: 'features.automations.conditionoperators.becameFilled',
    BecameEmpty: 'features.automations.conditionoperators.becameEmpty',
    IsTrue: 'features.automations.conditionoperators.isTrue',
    IsFalse: 'features.automations.conditionoperators.isFalse',
    DateIsToday: 'features.automations.conditionoperators.dateIsToday',
    DateIsOverdue: 'features.automations.conditionoperators.dateIsOverdue',
    DateIsWithinDays: 'features.automations.conditionoperators.dateIsWithinDays',
  };
  return i18n.t(map[operator], undefined, operator);
}

/**
 * Returns the set of valid operators for a given field type and trigger.
 * Hides irrelevant operators to keep UX clean.
 */
export function operatorsForFieldType(
  fieldType: string | undefined,
  triggerType?: WorkflowAutomationTriggerType
): WorkflowAutomationConditionOperator[] {
  const base: WorkflowAutomationConditionOperator[] = ['Equals', 'NotEquals'];
  const isTransitionTrigger = triggerType === 'CardFieldUpdated';

  if (fieldType === 'BOOLEAN' || fieldType === 'CHECKBOX' || fieldType === 'CONFIRMATION_DATE') {
    return ['IsTrue', 'IsFalse'];
  }
  if (fieldType === 'DATE') {
    const dateOps: WorkflowAutomationConditionOperator[] = [
      ...base,
      'IsEmpty', 'IsNotEmpty',
      'DateIsToday', 'DateIsOverdue', 'DateIsWithinDays',
    ];
    if (isTransitionTrigger) {
      dateOps.push('BecameFilled', 'BecameEmpty');
    }
    return dateOps;
  }
  if (fieldType === 'NUMBER' || fieldType === 'CURRENCY' || fieldType === 'PROGRESS') {
    const numOps: WorkflowAutomationConditionOperator[] = [
      ...base,
      'GreaterThan', 'LessThan',
      'GreaterThanOrEqual', 'LessThanOrEqual',
      'IsEmpty', 'IsNotEmpty',
    ];
    if (isTransitionTrigger) {
      numOps.push('ChangedTo', 'BecameFilled', 'BecameEmpty');
    }
    return numOps;
  }

  const ops: WorkflowAutomationConditionOperator[] = [
    ...base, 'Contains', 'NotContains', 'IsEmpty', 'IsNotEmpty',
  ];
  if (isTransitionTrigger) {
    ops.push('ChangedTo', 'ChangedFromTo', 'BecameFilled', 'BecameEmpty');
  }
  return ops;
}

/**
 * Human-readable label for the scheduling config.
 */
export function scheduleDisplayText(
  i18n: I18nService,
  config: AutomationDelayConfig | null | undefined,
  dateFieldName?: string
): string {
  if (!config || config.mode === 'Immediate') {
    return i18n.t('features.automations.schedule.immediately', undefined, 'Executar imediatamente');
  }
  if (config.mode === 'RelativeToEvent') {
    const amount = config.delayAmount ?? 0;
    const unit = config.delayUnit ?? 'Minutes';
    const unitLabel = i18n.t(`features.automations.schedule.unit.${unit.toLowerCase()}`, { count: amount });
    return i18n.t('features.automations.schedule.afterDelay', { amount, unit: unitLabel },
      `Executar ${amount} ${unitLabel} após o gatilho`);
  }
  if (config.mode === 'RelativeToDateField') {
    const amount = config.delayAmount ?? 0;
    const unit = config.delayUnit ?? 'Days';
    const relation = config.dateRelation ?? 'Before';
    const unitLabel = i18n.t(`features.automations.schedule.unit.${unit.toLowerCase()}`, { count: amount });
    const relationLabel = i18n.t(`features.automations.schedule.relation.${relation.toLowerCase()}`);
    const fieldLabel = dateFieldName || i18n.t('features.automations.schedule.dateField');
    if (relation === 'OnDay') {
      return i18n.t('features.automations.schedule.onDateField', { field: fieldLabel },
        `Executar no dia de "${fieldLabel}"`);
    }
    return i18n.t('features.automations.schedule.relativeToField',
      { amount, unit: unitLabel, relation: relationLabel, field: fieldLabel },
      `Executar ${amount} ${unitLabel} ${relationLabel} "${fieldLabel}"`);
  }
  return '';
}

/**
 * Human-readable label for the safety mode.
 */
export function safetyModeLabel(
  i18n: I18nService,
  mode: AutomationSafetyMode | null | undefined
): string {
  if (!mode || mode === 'ExecuteAlways') {
    return i18n.t('features.automations.safety.executeAlways', undefined, 'Executar sempre');
  }
  if (mode === 'ExecuteOnlyClear') {
    return i18n.t('features.automations.safety.executeOnlyClear', undefined, 'Somente se houver alvo claro');
  }
  if (mode === 'AbortOnAmbiguity') {
    return i18n.t('features.automations.safety.abortOnAmbiguity', undefined, 'Abortar em caso de ambiguidade');
  }
  return '';
}

/**
 * Builds a complete human-readable summary of the automation.
 * E.g.: "Quando card for criado, se prioridade = alta, definir responsável João"
 */
export function buildFullAutomationSummary(
  i18n: I18nService,
  automation: {
    triggerType: WorkflowAutomationTriggerType;
    actionType: WorkflowAutomationActionType;
    actionConfigJson: Record<string, unknown> | null;
    triggerConfigJson?: Record<string, unknown> | null;
    delayConfig?: AutomationDelayConfig | null;
  },
  conditions: Array<WorkflowAutomationConditionModel | CreateWorkflowAutomationConditionRequestModel>,
  context?: ActionDisplayContext & ConditionDisplayContext
): string {
  const parts: string[] = [];

  const trigger = automation.triggerType === 'DailyAgenda'
    ? i18n.t('dailyagenda.summaryTime', { time: String(automation.triggerConfigJson?.['localTime'] ?? automation.triggerConfigJson?.['LocalTime'] ?? '09:30') })
    : triggerLabel(i18n, automation.triggerType);
  parts.push(automation.triggerType === 'DailyAgenda' ? trigger : i18n.t('features.automations.fullSummary.when', { trigger }, `Quando ${trigger}`));

  if (conditions.length > 0) {
    const condTexts = conditions.map((c) => {
      if ('id' in c) {
        return conditionDisplayText(i18n, c as WorkflowAutomationConditionModel, context ?? {});
      }
      const opLabel = conditionOperatorLabel(i18n, c.operator);
      if (c.field === 'CardField' && c.value && typeof c.value === 'object') {
        const payload = c.value as Record<string, unknown>;
        const fieldId = typeof payload['workflowFieldId'] === 'string' ? payload['workflowFieldId'] : '';
        const fieldName = context?.workflowFields?.find((f) => f.fieldId === fieldId)?.name ?? fieldId;
        const innerVal = payload['value'] !== null && payload['value'] !== undefined ? String(payload['value']) : '';
        return `"${fieldName || '-'}" ${opLabel} ${innerVal}`.trim();
      }
      const fieldLabel = conditionFieldLabel(i18n, c.field);
      const val = c.value === null || c.value === undefined ? '' : String(c.value);
      return `${fieldLabel} ${opLabel} ${val}`.trim();
    });
    const condJoined = condTexts.join(` ${i18n.t('common.and', undefined, 'e')} `);
    parts.push(i18n.t('features.automations.fullSummary.if', { conditions: condJoined },
      `se ${condJoined}`));
  }

  if (automation.delayConfig && automation.delayConfig.mode !== 'Immediate') {
    parts.push(scheduleDisplayText(i18n, automation.delayConfig));
  }

  const action = automation.triggerType === 'DailyAgenda'
    ? i18n.t('dailyagenda.summaryAction')
    : actionDisplayText(i18n, automation.actionType, automation.actionConfigJson, context);
  parts.push(action);

  return parts.join(', ');
}

export function automationSummary(i18n: I18nService, automation: WorkflowAutomationModel): string {
  return i18n.t('features.automations.summary.pattern', {
    trigger: triggerLabel(i18n, automation.triggerType),
    action: actionLabel(i18n, automation.actionType),
  });
}

export function automationListSummary(i18n: I18nService, automation: WorkflowAutomationModel): string {
  return i18n.t('features.automations.summary.listline', {
    trigger: triggerLabel(i18n, automation.triggerType),
    action: actionLabel(i18n, automation.actionType),
  });
}

export function actionDisplayText(
  i18n: I18nService,
  actionType: WorkflowAutomationActionType,
  actionConfig: Record<string, unknown> | null,
  context?: ActionDisplayContext
): string {
  const config = actionConfig ?? {};
  if (actionType === 'MoveCardToStage') {
    const stageId = typeof config['targetStageId'] === 'string' ? config['targetStageId'] : '';
    const stageName = context?.workflowStages?.find((item) => item.id === stageId)?.name ?? stageId;
    return i18n.t('features.automations.summary.actionMoveCardToStage', {
      stage: stageName || '-',
    });
  }
  if (actionType === 'CreateCardComment') {
    const message = typeof config['message'] === 'string' ? config['message'] : '';
    return i18n.t('features.automations.summary.actionCreateCardComment', {
      message: message || '-',
    });
  }
  if (actionType === 'AssignUserToCard') {
    const userId = typeof config['userId'] === 'string' ? config['userId'] : '';
    const userName = context?.members?.find((item) => item.id === userId)?.name ?? (userId && !isLikelyGuid(userId) ? userId : '-');
    return i18n.t('features.automations.summary.actionAssignUserToCard', {
      user: userName || '-',
    });
  }
  if (actionType === 'SendWhatsAppTemplate') {
    const templateKey = typeof config['templateKey'] === 'string' ? config['templateKey'].trim() : '';
    const templateName = whatsappTemplateDisplayName(i18n, templateKey, context?.whatsAppTemplates);
    if (!isWhatsAppActionConfigReady(config, context?.whatsAppTemplates)) {
      return i18n.t('features.automations.summary.whatsappIncomplete');
    }
    return i18n.t('features.automations.summary.actionSendWhatsappConfigured', { template: templateName });
  }
  if (actionType === 'SendEmailTemplate') {
    const templateKey = typeof config['templateKey'] === 'string' ? config['templateKey'].trim() : '';
    const templateName = emailTemplateDisplayName(templateKey, context?.emailTemplates);
    if (!isEmailAutomationConfigComplete(config, context?.emailTemplates)) {
      return i18n.t('features.automations.summary.emailIncomplete');
    }
    const to = emailRecipientSummaryLine(i18n, config, context?.workflowFields);
    return i18n.t('features.automations.summary.actionSendEmailConfigured', { template: templateName, to });
  }
  if (actionType === 'UpdateCardField') {
    const fid = typeof config['workflowFieldId'] === 'string' ? config['workflowFieldId'] : '';
    const fname = context?.workflowFields?.find((f) => f.fieldId === fid)?.name ?? (fid && !isLikelyGuid(fid) ? fid : '-');
    const vsRaw = config['valueSource'];
    const vs = typeof vsRaw === 'string' ? vsRaw
      : typeof vsRaw === 'number' ? ({ 1: 'Static', 2: 'CardField', 3: 'Expression' }[vsRaw] ?? '')
      : '';
    let sourceHint = '';
    if (vs === 'CardField') {
      const sfid = typeof config['sourceFieldId'] === 'string' ? config['sourceFieldId'] : '';
      const sfname = context?.workflowFields?.find((f) => f.fieldId === sfid)?.name ?? '';
      sourceHint = sfname ? ` ← "${sfname}"` : '';
    } else if (vs === 'Expression') {
      sourceHint = ` (${i18n.t('features.automations.updateField.srcExpression', undefined, 'expressão')})`;
    }
    return i18n.t('features.automations.summary.actionUpdateField', { field: fname || '-' }) + sourceHint;
  }
  if (actionType === 'CreateCard') {
    const title =
      typeof config['title'] === 'string'
        ? config['title']
        : typeof config['cardTitle'] === 'string'
          ? config['cardTitle']
          : '-';
    return i18n.t('features.automations.summary.actionCreateCard', { title: title || '-' });
  }
  if (actionType === 'SendAppNotification') {
    const title = typeof config['title'] === 'string' ? config['title'].trim() : '';
    return i18n.t('features.automations.summary.actionSendAppNotification', { title: title || '-' });
  }
  return actionLabel(i18n, actionType);
}

export function whatsappActionShortLabel(
  i18n: I18nService,
  actionConfig: Record<string, unknown> | null
): string {
  const config = actionConfig ?? {};
  const templateKey = typeof config['templateKey'] === 'string' ? config['templateKey'] : '';
  return templateKey
    ? i18n.t('features.automations.summary.whatsappShortWithTemplate', { template: templateKey })
    : i18n.t('features.automations.summary.whatsappShort');
}

export function whatsAppOperationalStatusKey(
  actionConfig: Record<string, unknown> | null,
  templates?: readonly WhatsAppTemplateModel[] | null
): string {
  const config = actionConfig ?? {};
  const templateKey = typeof config['templateKey'] === 'string' ? config['templateKey'].trim() : '';
  if (!templateKey) return 'features.automations.whatsapp.incomplete';
  if (isWhatsAppActionConfigReady(config, templates)) return 'features.automations.whatsapp.ready';
  if (!isWhatsAppDestinationComplete(config)) return 'features.automations.whatsapp.invaliddestination';
  if (!hasWhatsAppVariablesFilled(config, templates)) {
    return 'features.automations.whatsapp.missingrequiredvariables';
  }
  return 'features.automations.whatsapp.incomplete';
}

export function actionStateLabel(
  i18n: I18nService,
  actionType: WorkflowAutomationActionType,
  actionConfig: Record<string, unknown> | null,
  whatsAppTemplates?: readonly WhatsAppTemplateModel[] | null,
  emailTemplates?: readonly EmailTemplateRefModel[] | null
): string {
  return actionIsConfigured(actionType, actionConfig, whatsAppTemplates, emailTemplates)
    ? i18n.t('features.automations.action.state.configured')
    : i18n.t('features.automations.action.state.incomplete');
}

export function actionIsConfigured(
  actionType: WorkflowAutomationActionType,
  actionConfig: Record<string, unknown> | null,
  whatsAppTemplates?: readonly WhatsAppTemplateModel[] | null,
  emailTemplates?: readonly EmailTemplateRefModel[] | null
): boolean {
  return isActionConfigComplete(actionType, actionConfig, whatsAppTemplates, emailTemplates);
}

export function actionDetailLines(
  i18n: I18nService,
  actionType: WorkflowAutomationActionType,
  actionConfig: Record<string, unknown> | null,
  context?: ActionDisplayContext
): Array<{ label: string; value: string }> {
  const config = actionConfig ?? {};
  if (actionType === 'MoveCardToStage') {
    const stageId = typeof config['targetStageId'] === 'string' ? config['targetStageId'] : '';
    const stageName = context?.workflowStages?.find((item) => item.id === stageId)?.name ?? (stageId && !isLikelyGuid(stageId) ? stageId : '-');
    return [{ label: i18n.t('features.automations.actionconfig.targetstageid'), value: stageName }];
  }
  if (actionType === 'CreateCardComment') {
    const message = typeof config['message'] === 'string' ? config['message'] : '-';
    return [{ label: i18n.t('features.automations.actionconfig.message'), value: message }];
  }
  if (actionType === 'AssignUserToCard') {
    const userId = typeof config['userId'] === 'string' ? config['userId'] : '';
    const userName = context?.members?.find((item) => item.id === userId)?.name ?? (userId && !isLikelyGuid(userId) ? userId : '-');
    return [{ label: i18n.t('features.automations.actionconfig.userid'), value: userName }];
  }
  if (actionType === 'SendWhatsAppTemplate') {
    const templateKey = typeof config['templateKey'] === 'string' ? config['templateKey'].trim() : '';
    const targetPhone = typeof config['targetPhone'] === 'string' ? config['targetPhone'] : '-';
    const varsSource =
      config['variables'] && typeof config['variables'] === 'object'
        ? (config['variables'] as Record<string, unknown>)
        : {};
    const requiredKeys = resolveExpectedWhatsAppVariableKeys(templateKey, context?.whatsAppTemplates);
    const requiredCount = requiredKeys.length;
    const dest = normalizeWhatsAppDestinationType(
      config['destinationType'] ?? config['recipientType'],
      typeof config['targetPhone'] === 'string' ? config['targetPhone'] : ''
    );
    const filledCount =
      requiredCount > 0
        ? requiredKeys.filter((key) => {
            const raw = varsSource[key];
            if (typeof raw !== 'string') return false;
            return isWhatsAppVariableSlotValueValid(key, raw, dest);
          }).length
        : 0;
    const isReady = isWhatsAppActionConfigReady(config, context?.whatsAppTemplates);
    const templateLabel = whatsappTemplateDisplayName(i18n, templateKey, context?.whatsAppTemplates);
    const lines: Array<{ label: string; value: string }> = [
      { label: i18n.t('features.automations.whatsapp.template'), value: templateLabel },
      { label: i18n.t('features.automations.whatsapp.targetphone'), value: targetPhone },
      {
        label: i18n.t('features.automations.whatsapp.variables.requiredcount'),
        value: `${requiredCount}`,
      },
      {
        label: i18n.t('features.automations.whatsapp.variables.filledcount'),
        value: `${filledCount}`,
      },
      {
        label: i18n.t('features.automations.whatsapp.preview.configurationstatus'),
        value: isReady
          ? i18n.t('features.automations.whatsapp.status.ready')
          : i18n.t('features.automations.whatsapp.status.incomplete'),
      },
    ];
    return lines;
  }
  if (actionType === 'SendEmailTemplate') {
    const templateKey = typeof config['templateKey'] === 'string' ? config['templateKey'].trim() : '';
    const templateLabel = emailTemplateDisplayName(templateKey, context?.emailTemplates);
    const dest = normalizeEmailRecipientType(config['recipientType'] ?? config['destinationType']);
    const ready = isEmailAutomationConfigComplete(config, context?.emailTemplates);
    const lines: Array<{ label: string; value: string }> = [
      { label: i18n.t('features.automations.emailAction.template'), value: templateLabel },
      {
        label: i18n.t('features.automations.emailAction.recipientType'),
        value: i18n.t(`features.automations.emailAction.rtLabel.${dest}`),
      },
      {
        label: i18n.t('features.automations.emailAction.destination'),
        value: emailRecipientSummaryLine(i18n, config, context?.workflowFields),
      },
      {
        label: i18n.t('features.automations.whatsapp.preview.configurationstatus'),
        value: ready
          ? i18n.t('features.automations.whatsapp.status.ready')
          : i18n.t('features.automations.whatsapp.status.incomplete'),
      },
    ];
    return lines;
  }
  if (actionType === 'UpdateCardField') {
    const fid = typeof config['workflowFieldId'] === 'string' ? config['workflowFieldId'] : '';
    const fieldMeta = context?.workflowFields?.find((item) => item.fieldId === fid);
    const fname = fieldMeta?.name ?? (fid && !isLikelyGuid(fid) ? fid : '-');
    const isPerson = fieldMeta?.type === 'PERSON' || fieldMeta?.type === 'USER';
    const lines: Array<{ label: string; value: string }> = [
      { label: i18n.t('features.automations.actionconfig.fieldToUpdate'), value: fname },
    ];

    const vsRaw = config['valueSource'];
    const vs = typeof vsRaw === 'string' ? vsRaw
      : typeof vsRaw === 'number' ? ({ 1: 'Static', 2: 'CardField', 3: 'Expression' }[vsRaw] ?? 'Static')
      : 'Static';

    if (vs === 'CardField') {
      const sfid = typeof config['sourceFieldId'] === 'string' ? config['sourceFieldId'] : '';
      const sfname = context?.workflowFields?.find((f) => f.fieldId === sfid)?.name ?? (sfid && !isLikelyGuid(sfid) ? sfid : '-');
      lines.push({ label: i18n.t('features.automations.actionconfig.newValue'), value: `${i18n.t('features.automations.updateField.srcCardField', undefined, 'Copiar de campo')}: "${sfname}"` });
    } else if (vs === 'Expression') {
      lines.push({ label: i18n.t('features.automations.actionconfig.newValue'), value: i18n.t('features.automations.updateField.srcExpression', undefined, 'Expressão calculada') });
    } else {
      const valRaw = config['staticValue'] ?? config['value'];
      let vstr: string;
      if (valRaw === null || valRaw === undefined) {
        vstr = '-';
      } else if (typeof valRaw === 'object') {
        vstr = '-';
      } else {
        vstr = String(valRaw);
      }
      if (isPerson && vstr !== '-' && vstr.trim().length > 0) {
        const uid = vstr.trim();
        const memberName = context?.members?.find((m) => m.id === uid)?.name;
        vstr = memberName ?? uid;
      }
      lines.push({ label: i18n.t('features.automations.actionconfig.newValue'), value: vstr || '-' });
    }

    const targetRaw = config['target'];
    const target = typeof targetRaw === 'number' ? (targetRaw === 2 ? 'LookedUpCard' : 'CurrentCard')
      : typeof targetRaw === 'string' ? targetRaw : 'CurrentCard';
    if (target === 'LookedUpCard') {
      lines.push({ label: i18n.t('features.automations.updateField.targetTitle', undefined, 'Alvo'), value: i18n.t('features.automations.updateField.targetAnotherCard', undefined, 'Outro card encontrado') });
    }

    return lines;
  }
  if (actionType === 'SetCardDueDate') {
    const mode = typeof config['mode'] === 'string' ? config['mode'] : '';
    if (mode === 'relative') {
      const amount = typeof config['value'] === 'number' ? config['value'] : 0;
      const unit = typeof config['unit'] === 'string' ? config['unit'] : 'Hours';
      const unitLabel = i18n.t(`features.automations.schedule.unit.${unit.toLowerCase()}`, { count: amount }, unit);
      return [{ label: i18n.t('features.automations.actionconfig.dueDate'), value: `${amount} ${unitLabel} ${i18n.t('features.automations.dueDate.afterExecution', undefined, 'após a execução')}` }];
    }
    const raw = config['dueDate'];
    let value = '-';
    if (raw === null || (typeof raw === 'string' && !raw.trim())) {
      value = i18n.t('features.automations.summary.dueDateCleared');
    } else if (typeof raw === 'string') {
      const d = new Date(raw.trim());
      value = Number.isNaN(d.getTime()) ? raw.trim() : d.toLocaleString();
    }
    return [{ label: i18n.t('features.automations.actionconfig.dueDate'), value }];
  }
  return [];
}

export function conditionDisplayText(
  i18n: I18nService,
  condition: WorkflowAutomationConditionModel,
  context: ConditionDisplayContext
): string {
  return conditionMediumLabel(i18n, condition, context);
}

const NO_VALUE_OPERATORS: WorkflowAutomationConditionOperator[] = [
  'BecameFilled', 'BecameEmpty', 'IsTrue', 'IsFalse',
  'IsEmpty', 'IsNotEmpty', 'DateIsToday', 'DateIsOverdue',
];

export function isConditionValid(condition: WorkflowAutomationConditionModel): boolean {
  if (!condition.field || !condition.operator) return false;
  const op = condition.operator as WorkflowAutomationConditionOperator;

  if (condition.field === 'CardComments') return isCommentConditionValid(condition.value, op);
  if (NO_VALUE_OPERATORS.includes(op)) return true;

  if (condition.field === 'CardField') {
    const source =
      condition.value && typeof condition.value === 'object'
        ? (condition.value as Record<string, unknown>)
        : null;
    const fieldId = typeof source?.['workflowFieldId'] === 'string' ? source['workflowFieldId'].trim() : '';
    if (!fieldId) return false;
    if (op === 'ChangedTo') {
      const val = source?.['value'];
      return val !== null && val !== undefined && String(val).trim().length > 0;
    }
    if (op === 'ChangedFromTo') {
      const from = source?.['fromValue'];
      const to = source?.['toValue'];
      return from !== null && from !== undefined && String(from).trim().length > 0
        && to !== null && to !== undefined && String(to).trim().length > 0;
    }
    if (op === 'DateIsWithinDays') {
      const days = source?.['value'];
      return days !== null && days !== undefined && Number(days) > 0;
    }
    const value = source?.['value'];
    return value !== null && value !== undefined && String(value).trim().length > 0;
  }
  if (condition.field === 'CardStage') {
    return typeof condition.value === 'string' && condition.value.trim().length > 0;
  }
  if (condition.field === 'ChecklistCompleted') {
    return typeof condition.value === 'boolean';
  }
  return false;
}

export function conditionShortLabel(
  i18n: I18nService,
  condition: WorkflowAutomationConditionModel,
  context: ConditionDisplayContext
): string {
  if (condition.field === 'CardComments') return commentConditionLabel(i18n, condition.value, condition.operator, context.workflowFields);
  if (condition.field === 'CardField') {
    const source = asRecord(condition.value);
    const fieldId = typeof source['workflowFieldId'] === 'string' ? source['workflowFieldId'] : '';
    const fieldName = context.workflowFields?.find((item) => item.fieldId === fieldId)?.name ?? fieldId;
    return i18n.t('features.automations.conditions.short.cardfield', { field: fieldName || '-' });
  }
  if (condition.field === 'CardStage') {
    return i18n.t('features.automations.conditions.short.cardstage');
  }
  return i18n.t('features.automations.conditions.short.checklist');
}

export function conditionMediumLabel(
  i18n: I18nService,
  condition: WorkflowAutomationConditionModel,
  context: ConditionDisplayContext
): string {
  const op = condition.operator as WorkflowAutomationConditionOperator;
  const opLabel = conditionOperatorLabel(i18n, op);
  if (condition.field === 'CardComments') return commentConditionLabel(i18n, condition.value, condition.operator, context.workflowFields);

  if (condition.field === 'CardField') {
    const source = asRecord(condition.value);
    const fieldId = typeof source['workflowFieldId'] === 'string' ? source['workflowFieldId'] : '';
    const fieldName = context.workflowFields?.find((item) => item.fieldId === fieldId)?.name ?? fieldId;
    const name = fieldName || i18n.t('features.automations.conditions.field');

    if (NO_VALUE_OPERATORS.includes(op)) {
      return `"${name}" ${opLabel}`;
    }
    if (op === 'ChangedFromTo') {
      const from = readConditionValue(i18n, source['fromValue']);
      const to = readConditionValue(i18n, source['toValue']);
      return `"${name}" ${i18n.t('features.automations.conditionoperators.changedFromLabel', undefined, 'mudou de')} ${from} ${i18n.t('features.automations.conditionoperators.changedToLabel', undefined, 'para')} ${to}`;
    }
    if (op === 'DateIsWithinDays') {
      const days = readConditionValue(i18n, source['value']);
      return `"${name}" ${i18n.t('features.automations.conditionoperators.dateIsWithinDaysLabel', { days }, `nos próximos ${days} dias`)}`;
    }
    const value = readConditionValue(i18n, source['value']);
    return `"${name}" ${opLabel} ${value}`;
  }
  if (condition.field === 'CardStage') {
    const stageId = typeof condition.value === 'string' ? condition.value : '';
    const stageName = context.workflowStages?.find((item) => item.id === stageId)?.name ?? stageId ?? '-';
    return `${i18n.t('features.automations.conditionfields.cardstage')} ${opLabel} ${stageName}`;
  }
  const value = condition.value === true ? i18n.t('common.yes', undefined, 'sim') : i18n.t('common.no', undefined, 'nao');
  return `${i18n.t('features.automations.conditionfields.checklistcompleted')} ${opLabel} ${value}`;
}

export function conditionDetailLines(
  i18n: I18nService,
  condition: WorkflowAutomationConditionModel,
  context: ConditionDisplayContext
): Array<{ label: string; value: string }> {
  if (condition.field === 'CardComments') return [{ label: i18n.t('features.automations.comments.label'),
    value: commentConditionLabel(i18n, condition.value, condition.operator, context.workflowFields) }];
  if (condition.field === 'CardField') {
    const source = asRecord(condition.value);
    const fieldId = typeof source['workflowFieldId'] === 'string' ? source['workflowFieldId'] : '';
    const fieldName = context.workflowFields?.find((item) => item.fieldId === fieldId)?.name ?? fieldId ?? '-';
    return [
      { label: i18n.t('features.automations.conditions.detail.field'), value: fieldName || '-' },
      {
        label: i18n.t('features.automations.conditions.detail.operator'),
        value: conditionOperatorLabel(i18n, condition.operator as WorkflowAutomationConditionOperator),
      },
      {
        label: i18n.t('features.automations.conditions.detail.value'),
        value: readConditionValue(i18n, source['value']),
      },
    ];
  }
  if (condition.field === 'CardStage') {
    const stageId = typeof condition.value === 'string' ? condition.value : '';
    const stageName = context.workflowStages?.find((item) => item.id === stageId)?.name ?? stageId ?? '-';
    return [{ label: i18n.t('features.automations.conditions.detail.stage'), value: stageName }];
  }
  return [
    {
      label: i18n.t('features.automations.conditions.detail.checklist'),
      value: condition.value === true ? i18n.t('common.yes', undefined, 'sim') : i18n.t('common.no', undefined, 'nao'),
    },
  ];
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

/** Detecta se string parece ser um GUID para evitar exibir IDs brutos ao usuário. */
function isLikelyGuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
}

function readConditionValue(i18n: I18nService, value: unknown): string {
  if (typeof value === 'boolean') return value ? i18n.t('common.yes', undefined, 'sim') : i18n.t('common.no', undefined, 'nao');
  if (typeof value === 'number' || typeof value === 'string') return String(value);
  return '-';
}

function isActionConfigComplete(
  actionType: WorkflowAutomationActionType,
  actionConfig: Record<string, unknown> | null,
  whatsAppTemplates?: readonly WhatsAppTemplateModel[] | null,
  emailTemplates?: readonly EmailTemplateRefModel[] | null
): boolean {
  const config = actionConfig ?? {};
  if (actionType === 'MoveCardToStage') {
    return typeof config['targetStageId'] === 'string' && config['targetStageId'].trim().length > 0;
  }
  if (actionType === 'CreateCardComment') {
    return typeof config['message'] === 'string' && config['message'].trim().length > 0;
  }
  if (actionType === 'AssignUserToCard') {
    return typeof config['userId'] === 'string' && config['userId'].trim().length > 0;
  }
  if (actionType === 'SendWhatsAppTemplate') {
    return isWhatsAppActionConfigReady(config, whatsAppTemplates);
  }
  if (actionType === 'SendEmailTemplate') {
    return isEmailAutomationConfigComplete(config, emailTemplates);
  }
  if (actionType === 'UpdateCardField') {
    const fieldId = typeof config['workflowFieldId'] === 'string' ? config['workflowFieldId'].trim() : '';
    if (!fieldId) return false;
    const vsRaw = config['valueSource'];
    const vs = typeof vsRaw === 'string' ? vsRaw
      : typeof vsRaw === 'number' ? ({ 1: 'Static', 2: 'CardField', 3: 'Expression' }[vsRaw] ?? '')
      : '';
    if (vs === 'CardField') {
      return typeof config['sourceFieldId'] === 'string' && config['sourceFieldId'].trim().length > 0;
    }
    if (vs === 'Expression') {
      const expr = config['expression'];
      return !!expr && typeof expr === 'object';
    }
    const valueRaw = config['staticValue'] ?? config['value'];
    const valueStr =
      valueRaw === null || valueRaw === undefined ? '' : String(valueRaw).trim();
    return valueStr.length > 0;
  }
  if (actionType === 'SetCardDueDate') {
    if (!('dueDate' in config)) return false;
    const raw = config['dueDate'];
    if (raw === null || raw === undefined) return true;
    if (typeof raw === 'string' && !raw.trim()) return true;
    if (typeof raw === 'string') return !Number.isNaN(Date.parse(raw.trim()));
    return false;
  }
  if (actionType === 'CreateCard') {
    const title =
      typeof config['title'] === 'string'
        ? config['title'].trim()
        : typeof config['cardTitle'] === 'string'
          ? config['cardTitle'].trim()
          : '';
    const stageId = typeof config['targetStageId'] === 'string' ? config['targetStageId'].trim() : '';
    return !!title && !!stageId;
  }
  return false;
}
